import { Injectable } from '@angular/core'
import { Apollo, QueryRef } from 'apollo-angular'
import { DocumentNode } from 'graphql'
import gql from 'graphql-tag'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { get } from 'lodash-es'

export interface UserForTracking {
  id: number;
  email: string;
  fullName: string;
  role: 'customer' | 'writer' | 'admin';
  adminImpersonating: boolean;
  showTosMessage: boolean;
  intercomAttributes: object;
  account: {
    id: number;
    agency: boolean;
    createdAt: number;
  };
}

interface UserTrackingResult {
  currentUser: UserForTracking;
}

export interface User {
  id: number,
  email: string
}

export interface UserEmailResult {
  currentUser: User
}

export const UserTrackingQuery: DocumentNode = gql`
  query UserTrackingQuery {
    currentUser {
      id
      email
      fullName
      role
      adminImpersonating
      showTosMessage
      intercomAttributes
      account {
        id
        agency
        createdAt
      }
    }
  }
`

export type UserWithPermissions = {
  currentUser: {
    id: number;
    permissions: UserPermission[];
  };
};

export type UserPermission = {
  id: string;
  can: boolean;
  action: string;
  subject: string;
};

const userPermissionsQuery = gql`
  query UserPermissionsQuery {
    currentUser {
      id
      role
      permissions {
        id
        can
        action
        subject
      }
    }
  }
`

@Injectable({providedIn: 'root'})
export class UserQueries {

  constructor(private apollo: Apollo) {}

  userForTracking(): QueryRef<UserTrackingResult> {
    return this.apollo.watchQuery<UserTrackingResult>({
      query: UserTrackingQuery,
      fetchPolicy: 'cache-and-network'
    })
  }

  userEmailQuery(): Observable<string> {
    return this.apollo.query<UserEmailResult>({
      query: gql`
        query userEmailQuery {
          currentUser {
            id
            email
          }
        }
      `
    }).pipe(
      map(result => get(result, 'data.currentUser.email', ''))
    )
  }

  userPermissions(): Observable<UserWithPermissions> {
    return this.apollo.query<UserWithPermissions>({
      query: userPermissionsQuery
    }).pipe(map(result => result.data))
  }
}
